<main class="canvas">
  @if (release(); as r) {
    <!-- Showstopper banner (AC1) -->
    @if (showstopper(); as ss) {
      <div class="showstopper-banner">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2 1 21h22L12 2zm0 6 7.5 13h-15L12 8zm-1 3v5h2v-5h-2zm0 6v2h2v-2h-2z" />
        </svg>
        <span>
          <span class="strong"
            >{{ ss.count }} showstopper{{ ss.count === 1 ? '' : 's' }} flagged.</span
          >
          <span class="sub">
            Release cannot ship until resolved.
            @if (ss.owner) {
              Owned by {{ ss.owner }}.
            }
          </span>
        </span>
      </div>
    }

    <!-- Release summary (AC2) -->
    <section class="release-summary">
      <div class="release-summary-top">
        <div>
          <div class="eyebrow">Active release</div>
          <h1 class="release-name">{{ r.releaseName }}</h1>
        </div>
        <div class="summary-progress">
          <div class="overall-progress">
            <div class="overall-progress-bar">
              <div class="fill" [style.width.%]="stats().pct"></div>
              <div
                class="fail-fill"
                [style.left.%]="stats().pct"
                [style.width.%]="stats().failPct"
              ></div>
            </div>
            <div class="overall-progress-label">{{ stats().pct }}%</div>
          </div>
          <div class="complete-count">
            {{ stats().complete }} of {{ stats().total }} tests complete
          </div>
        </div>
      </div>

      <div class="release-stats">
        <div class="stat">
          <div class="stat-label">Passed</div>
          <div class="stat-value">
            <span class="num pass-num">{{ stats().passed }}</span>
            <span class="unit">tests</span>
          </div>
        </div>
        <div class="stat">
          <div class="stat-label">Failed</div>
          <div class="stat-value">
            <span class="num fail-num">{{ stats().failed }}</span>
            <span class="unit">tests</span>
          </div>
        </div>
        <div class="stat">
          <div class="stat-label">Remaining</div>
          <div class="stat-value">
            <span class="num">{{ stats().remaining }}</span>
            <span class="unit">tests</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Search on its own line (AC3) -->
    <div class="toolbar search-row">
      <div class="search-input">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="text"
          placeholder="Search test plans"
          [value]="search()"
          (input)="setSearch($any($event.target).value)"
          aria-label="Search test plans"
        />
        @if (search()) {
          <button
            type="button"
            class="search-clear"
            aria-label="Clear search"
            title="Clear search"
            (click)="setSearch('')"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        }
      </div>
    </div>

    <!-- Filter row: view chips + status chips + Export (AC3/AC4) -->
    <div class="toolbar filter-row">
      <div class="chip-group">
        <button type="button" class="chip active" aria-current="page">
          My view <span class="count">{{ myViewCount() }}</span>
        </button>
        @if (isManager()) {
          <a
            class="chip"
            [routerLink]="['/releases', r.releaseId, 'everyone']"
            (click)="chooseEveryoneView()"
          >
            Everyone <span class="count">{{ everyoneCount() }}</span>
          </a>
        } @else {
          <button type="button" class="chip" disabled title="Manager view (leads & admins only)">
            Everyone <span class="count">{{ everyoneCount() }}</span>
          </button>
        }
      </div>
      <div class="chip-group">
        @for (f of statusFilters; track f.key) {
          <button
            type="button"
            class="chip"
            [class.active]="statusFilter() === f.key"
            (click)="statusFilter.set(f.key)"
          >
            {{ f.label }}
          </button>
        }
      </div>
      <button class="btn btn-ghost export-btn" type="button" (click)="exportCsv()">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          width="14"
          height="14"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Export CSV
      </button>
    </div>

    <!-- Assigned to me (AC5) -->
    <div class="plan-section-head">
      <h2>
        Assigned to me <span class="count">{{ assignedToMe().length }} plans</span>
      </h2>
      <span class="hint">Your work for this release.</span>
    </div>
    <div class="plan-list">
      @for (p of assignedToMe(); track trackByPlanId($index, p)) {
        <ng-container
          *ngTemplateOutlet="planRow; context: { $implicit: p, mine: true }"
        ></ng-container>
      } @empty {
        <div class="plan-empty-state">
          <div class="empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M9 11l3 3 8-8" />
              <path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" />
            </svg>
          </div>
          @if (hasActiveFilters()) {
            <h4>No matching plans</h4>
            <p>Nothing assigned to you matches those filters.</p>
            <button type="button" class="btn btn-ghost btn-sm" (click)="resetFilters()">
              Clear filters
            </button>
          } @else {
            <h4>Nothing assigned to you yet</h4>
            <p>Claim a plan from the list below and it will show up here.</p>
          }
        </div>
      }
    </div>

    <!-- Unassigned (AC6) -->
    <div class="plan-section-head">
      <h2>
        Unassigned <span class="count">{{ unassigned().length }} plans</span>
      </h2>
      <span class="hint">These are up for grabs. Click the + avatar to claim one.</span>
    </div>
    <div class="plan-list">
      @for (p of unassigned(); track trackByPlanId($index, p)) {
        <ng-container
          *ngTemplateOutlet="planRow; context: { $implicit: p, mine: false }"
        ></ng-container>
      } @empty {
        <div class="plan-empty-state">
          <div class="empty-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          @if (hasActiveFilters()) {
            <h4>No matching plans</h4>
            <p>No unclaimed plans match those filters.</p>
            <button type="button" class="btn btn-ghost btn-sm" (click)="resetFilters()">
              Clear filters
            </button>
          } @else {
            <h4>Every plan is claimed</h4>
            <p>There's nothing left to pick up in this release.</p>
          }
        </div>
      }
    </div>
  } @else {
    <!-- The route resolver blocks navigation, so a null release here means the load
         failed or the release wasn't found (AC: failure state, not an endless spinner). -->
    <div class="release-error-state">
      <div class="error-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </div>
      <h2>We couldn't load this release</h2>
      <p>It may have been removed, or the server didn't respond. Try again in a moment.</p>
      <div class="error-actions">
        <button type="button" class="btn btn-primary" (click)="reload()">Try again</button>
        <a class="btn btn-ghost" routerLink="/releases">Back to releases</a>
      </div>
    </div>
  }
</main>

<!-- Plan row template (AC5/AC6/AC8/AC9) -->
<ng-template #planRow let-p let-mine="mine">
  <div class="plan-row" [class.has-showstopper]="p.numberOfShowstoppers > 0">
    <div class="plan-status {{ statusClass(p) }}" [title]="statusSub(p)">
      @switch (p.status) {
        @case ('Completed') {
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        }
        @case ('Failing') {
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
          >
            <path
              d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        }
        @case ('InProgress') {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
          </svg>
        }
        @default {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="12" cy="12" r="9" />
          </svg>
        }
      }
    </div>

    <div class="plan-info">
      <div class="plan-name">
        {{ p.testPlanName }}
        @if (p.numberOfShowstoppers > 0) {
          <span class="ss-pill">
            <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10" aria-hidden="true">
              <path d="M12 2 1 21h22L12 2zm-1 9v5h2v-5h-2zm0 6v2h2v-2h-2z" />
            </svg>
            Showstopper
          </span>
        }
      </div>
      <div class="plan-sub">{{ statusSub(p) }}</div>
    </div>

    <div class="plan-progress">
      <div class="mini-bar">
        <div class="fill" [style.width.%]="progressPct(p)"></div>
        @if (p.numberOfFailingTests > 0) {
          <div class="fail-fill" [style.left.%]="progressPct(p)" [style.width.%]="failPct(p)"></div>
        }
      </div>
      <div class="mini-count">{{ p.numberOfPassingTests }} / {{ p.numberOfTests }}</div>
    </div>

    <div class="plan-assignee">
      @if (mine) {
        <div class="assignee-avatar mine" [title]="'You'">{{ myInitials() }}</div>
        <span class="assignee-name mine">You</span>
      } @else if (isClaiming(p)) {
        <span class="assignee-avatar claiming" aria-hidden="true">
          <span class="spinner spinner-sm"></span>
        </span>
        <span class="assignee-name">Assigning…</span>
      } @else {
        <button
          class="assignee-avatar unassigned"
          type="button"
          title="Click to assign"
          [cdkMenuTriggerFor]="assignMenu"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            width="14"
            height="14"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
        <span class="assignee-name">Unassigned</span>
        <ng-template #assignMenu>
          <div class="popover assign-popover" cdkMenu>
            <button class="assign-me-btn" cdkMenuItem (click)="claim(p)">
              <span class="mini-avatar">{{ myInitials() }}</span>
              Assign to me
            </button>
            <div class="assign-note">
              Only leads and managers can assign plans to other testers.
            </div>
          </div>
        </ng-template>
      }
    </div>

    <button class="plan-cta" type="button" [disabled]="isOpening(p)" (click)="openPlan(p)">
      {{ isOpening(p) ? 'Opening…' : cta(p) }}
      @if (isOpening(p)) {
        <span class="spinner spinner-sm" aria-hidden="true"></span>
      } @else {
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          width="14"
          height="14"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      }
    </button>
  </div>
</ng-template>
